import { useCallback, useEffect, useRef, useState } from "react";
import { useTellescopeQueryClient, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { AnswerForType, Enduser } from "@tellescope/types-models";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { getFirstInsuranceAnswer } from "../../utils";
import type { EligibilityFieldType, EligibilityVendor } from "./eligibilityVendor";

export type EligibilityPhase = "idle" | "loading" | "error" | "result" | "cached";

/**
 * The answer both eligibility vendors write, which their two answer types share exactly.
 *
 * Declaring it once lets the shared hook build a result that satisfies either vendor's answer type
 * without asserting between them.
 */
export type EligibilityAnswerValue = AnswerForType["Candid Eligibility"] & AnswerForType["Stedi Eligibility"];

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const extractEligibilityResult = (
  responseData: unknown
): { status: string; benefits?: object; planMetadata?: object; errors?: unknown[] } | undefined => {
  if (!isObject(responseData)) return undefined;
  return {
    status: typeof responseData.status === "string" ? responseData.status : "UNKNOWN",
    benefits: isObject(responseData.benefits) ? responseData.benefits : undefined,
    planMetadata: isObject(responseData.planMetadata) ? responseData.planMetadata : undefined,
    errors: Array.isArray(responseData.errors) && responseData.errors.length ? responseData.errors : undefined,
  };
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return undefined;
};

export interface UseEligibilityFieldArgs<T extends EligibilityFieldType> {
  vendor: EligibilityVendor;
  value: EligibilityAnswerValue;
  onChange: (newVal: EligibilityAnswerValue) => void;
  options: TellescopeFormFieldBaseProps<T>["options"];
  enduserId: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  priorResponses: TellescopeFieldResponse[];
  sessionType?: "enduser" | "user";
  /** Links the vendor's usage record to this response, where the vendor bills per check. */
  formResponseId?: string;
}

export const useEligibilityField = <T extends EligibilityFieldType>({
  vendor,
  value,
  onChange,
  options,
  enduserId,
  priorResponses,
  sessionType = "enduser",
  formResponseId,
}: UseEligibilityFieldArgs<T>) => {
  const { fetchQuery } = useTellescopeQueryClient();

  const insuranceAnswer = getFirstInsuranceAnswer(priorResponses);
  const payerId = insuranceAnswer?.payerId;
  const memberId = insuranceAnswer?.memberId;
  const payerName = insuranceAnswer?.payerName;

  const isCached = !!(value?.status && value.payerId === payerId);

  const [phase, setPhase] = useState<EligibilityPhase>("idle");
  const [result, setResult] = useState<EligibilityAnswerValue>();
  const [responseErrors, setResponseErrors] = useState<unknown[]>();
  const [errorMessage, setErrorMessage] = useState<string>();

  // A billed check must not be issued twice concurrently; fetchQuery always reaches the network, so
  // this guard — not the query cache — is what makes a double click safe.
  const isRunningRef = useRef(false);

  const latestRef = useRef({
    vendor,
    onChange,
    options,
    enduserId,
    payerId,
    memberId,
    payerName,
    formResponseId,
    fetchQuery,
  });
  latestRef.current = {
    vendor,
    onChange,
    options,
    enduserId,
    payerId,
    memberId,
    payerName,
    formResponseId,
    fetchQuery,
  };

  const runCheck = useCallback(async () => {
    if (isRunningRef.current) return;

    const { vendor, onChange, options, enduserId, payerId, memberId, payerName, formResponseId, fetchQuery } =
      latestRef.current;
    isRunningRef.current = true;
    setErrorMessage(undefined);
    setResponseErrors(undefined);
    setPhase("loading");

    try {
      const response = await fetchQuery({
        model: "integrations",
        method: "proxy_read",
        request: {
          id: enduserId,
          integration: vendor.integration,
          type: vendor.proxyType,
          query: JSON.stringify({
            serviceCode: vendor.serviceCodeOf(options),
            npi: vendor.npiOf(options),
            payerId,
            memberId,
            payerName,
            ...(formResponseId ? { formResponseId } : {}),
          }),
        },
      });

      const data = extractEligibilityResult(response?.data);
      setResponseErrors(data?.errors);
      setPhase("result");
      if (!data) return;

      const reported: EligibilityAnswerValue = {
        payerId,
        status: data.status,
        benefits: data.benefits,
        planMetadata: data.planMetadata,
      };
      setResult(reported);
      onChange(reported);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setPhase("error");
    } finally {
      isRunningRef.current = false;
    }
  }, []);

  const triggerCheck = useCallback(() => void runCheck(), [runCheck]);

  // The patient-facing check runs on its own; the guard ref (not the dependency list) is what keeps a
  // StrictMode double-invoke or a re-render from issuing a second billed call.
  const hasAutoRunRef = useRef(false);
  const shouldAutoRun = sessionType === "enduser" && !isCached;
  useEffect(() => {
    if (!shouldAutoRun || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    void runCheck();
  }, [runCheck, shouldAutoRun]);

  return {
    phase: isCached ? "cached" : phase,
    displayStatus: isCached ? value?.status : result?.status,
    payerName,
    payerId,
    memberId,
    errorMessage,
    responseErrors,
    isChecking: phase === "loading",
    triggerCheck,
  };
};
